import { Grid, Stack } from '@mui/material'
import {
  CargoType,
  CarrierType,
  CarrierVisitDirection,
  CarrierVisitStatus,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { IssueDashboardCard } from '@planning/components/IssueDashboardCard'
import { ExtendedViewSwitch } from '@planning/components/visits-dashboard/ExtendedViewSwitch'
import { InboundDashboardCard } from '@planning/components/visits-dashboard/InboundDashboardCard'
import { OutboundDashboardCard } from '@planning/components/visits-dashboard/OutboundDashboardCard'
import { BaplieParseResult } from '@planning/pages/Order/stores/OrderListUploadParsingStore'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { IVesselVisitItem, VesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { IFilterDelegate, ISortDelegate } from '@planning/stores/PaginatedLocalStore'
import { SortingModel } from '@planning/stores/PaginatedStore'
import { PATHS } from '@tom-ui/utils'
import dayjs from 'dayjs'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import moment from 'moment'
import { FC, useState } from 'react'
import { IDetailPages } from './CarrierVisitActionBar'
import { OrderListUploadDialogV2 } from './Upload/OrderListUploadDialog'
import { VisitBreak } from './VisitBreak'

export const vesselVisitSortingDelegate: ISortDelegate<IVesselVisitItem> = (sortingModel, a, b) => {
  const getValue = (item: IVesselVisitItem, sortingModel: SortingModel<IVesselVisitItem>) => {
    if (sortingModel.orderBy) {
      if (_.has(item, sortingModel.orderBy)) return _.get(item, sortingModel.orderBy)
      if (_.has(item.data, sortingModel.orderBy)) return _.get(item.data, sortingModel.orderBy)
    }
    return item.data.eta
  }

  const valA = getValue(a, sortingModel) as any
  const valB = getValue(b, sortingModel) as any
  let sort = 0
  if (moment(valA).isValid() && moment(valB).isValid()) {
    sort = moment(valA).diff(moment(valB))
  } else if (typeof valA === 'string' && typeof valB === 'string') {
    sort = valA.localeCompare(valB)
  }

  return sortingModel.isDescending ? -sort : sort
}

export const vesselVisitFilterDelegate: IFilterDelegate<IVesselVisitItem> = (
  filter: string,
  item: IVesselVisitItem,
  showCompleted?: boolean,
  from?: Date,
  to?: Date,
  filterStatus?: string,
) => {
  const filterableProperties: string[] = ['data.cargoType', 'data.name', 'data.shippingLine']

  if (!showCompleted && item.data.status === CarrierVisitStatus.Departed) return false

  if (filterStatus && item.data.status !== filterStatus) {
    return false
  }

  if (dayjs(from) > dayjs(item.arrival) || dayjs(item.arrival) > dayjs(to)) {
    return false
  }

  if (filter !== '') {
    return filterableProperties
      .map(p => p as keyof VesselVisitItem)
      .some(p => {
        const prop = _.get(item, p)
        const vessel = _.map(item.vessels, p)[0]

        if (vessel) {
          return vessel.toLowerCase().includes(filter.toLowerCase())
        }

        if (typeof prop === 'string') return prop.toLowerCase().includes(filter.toLowerCase())
        if (typeof prop === 'number') return prop === parseFloat(filter)

        return false
      })
  }

  return true
}

export const vesselVisitDetailPages = {
  dashboard: 'dashboard',
  discharge: PATHS.visits.vesselVisits.discharge.url,
  load: PATHS.visits.vesselVisits.load.url,
  restow: PATHS.visits.vesselVisits.restow.url,
  hatchcovers: PATHS.visits.vesselVisits.hatchCovers.url,
  stoppages: PATHS.visits.vesselVisits.stoppages.url,
} as const

export type CarrierVisitDashboardProps = {
  visitType: CarrierType
  detailPages?: IDetailPages
  vesselVisitItem?: IVesselVisitItem
  railVisitItem?: IRailVisitItem
  handlePageChange: (page: string) => void
  onClickExportOrders?: (direction: CarrierVisitDirection) => void
  unresolvedOperationsIssueCount?: number
  parseBaplie?: (file: File) => Promise<BaplieParseResult>
  equipmentPlanningSummary?: React.ReactElement
}

export const CarrierVisitDashboard: FC<CarrierVisitDashboardProps> = observer(
  ({
    vesselVisitItem,
    railVisitItem,
    visitType,
    detailPages,
    handlePageChange,
    onClickExportOrders,
    unresolvedOperationsIssueCount,
    parseBaplie,
    equipmentPlanningSummary,
  }) => {
    const { orderListUploadViewStoreV2 } = usePlanningStore()
    const [isMinimalisticView, setIsMinimalisticView] = useState(true)

    if (!vesselVisitItem && !railVisitItem) return <></>
    const selectDetailPages = detailPages ?? vesselVisitDetailPages

    const currentVisit = visitType === CarrierType.Train ? railVisitItem : vesselVisitItem

    const isContainerType =
      currentVisit?.data.cargoType && currentVisit?.data.cargoType !== CargoType.GeneralCargo

    return (
      <Grid container justifyContent='flex-start' spacing={{ xs: 1, md: 3 }} paddingX='1.5rem'>
        {isContainerType && (
          <Grid paddingTop='0 !important' size={12}>
            <Stack alignItems='end'>
              <ExtendedViewSwitch
                value={!isMinimalisticView}
                onChange={() => setIsMinimalisticView(!isMinimalisticView)}
              />
            </Stack>
          </Grid>
        )}

        <Grid
          size={{
            sm: 12,
            md: 6,
            lg: 4,
          }}
        >
          <InboundDashboardCard
            visitId={(railVisitItem ?? vesselVisitItem)?.data.id}
            orders={(railVisitItem ?? vesselVisitItem)?.discharge.orders ?? []}
            visitType={visitType}
            visitStatus={(railVisitItem ?? vesselVisitItem)?.data.status}
            cargoType={(railVisitItem ?? vesselVisitItem)?.data.cargoType}
            isMinimalisticView={isMinimalisticView}
            onViewClick={() => handlePageChange(selectDetailPages.discharge)}
            onClickExportOrders={onClickExportOrders}
          />
        </Grid>
        <Grid
          size={{
            sm: 12,
            md: 6,
            lg: 4,
          }}
        >
          <OutboundDashboardCard
            visitId={(railVisitItem ?? vesselVisitItem)?.data.id}
            orders={(railVisitItem ?? vesselVisitItem)?.load.orders ?? []}
            visitType={visitType}
            visitStatus={(railVisitItem ?? vesselVisitItem)?.data.status}
            cargoType={(railVisitItem ?? vesselVisitItem)?.data.cargoType}
            isMinimalisticView={isMinimalisticView}
            onViewClick={() => handlePageChange(selectDetailPages.load)}
            onClickExportOrders={onClickExportOrders}
          />
        </Grid>
        <OrderListUploadDialogV2
          enableFieldSelectionFromOrdersView={false}
          open={orderListUploadViewStoreV2.open}
          handleDialogClose={() => {
            orderListUploadViewStoreV2.setOpen(false)
          }}
          visitType={visitType}
          parseBaplie={parseBaplie}
        />

        <Grid
          size={{
            sm: 12,
            md: 6,
            lg: 4,
          }}
        >
          <IssueDashboardCard
            visitType={visitType}
            vesselVisitItem={vesselVisitItem}
            railVisitItem={railVisitItem}
            onViewClick={() => handlePageChange(`${PATHS.issues.url}`)}
            unresolvedOperationsIssueCount={unresolvedOperationsIssueCount}
          />
        </Grid>

        <Grid
          size={{
            sm: 12,
            md: 6,
            lg: 4,
          }}
        >
          <VisitBreak vesselVisitItem={vesselVisitItem} railVisitItem={railVisitItem} />
        </Grid>
        {equipmentPlanningSummary && isContainerType && (
          <Grid
            size={{
              sm: 12,
              md: 6,
              lg: 8,
            }}
          >
            {equipmentPlanningSummary}
          </Grid>
        )}
      </Grid>
    )
  },
)
